<template>
  <v-container class="time-clock">
    <v-row class="mb-4">
      <v-col cols="12">
        <h1>Time Clock</h1>
        <p class="text-body1 text-medium-emphasis">Clock in and out of your shifts</p>
      </v-col>
    </v-row>

    <v-alert v-if="pageError || store.error" type="error" closable class="mb-4" @click:close="dismissErrors">
      {{ pageError || store.error }}
    </v-alert>

    <!-- Currently clocked in -->
    <v-card v-if="store.myOpenEntry" color="success" variant="tonal" class="mb-6">
      <v-card-item>
        <template #title>You're clocked in</template>
        <template #subtitle>{{ shiftLabel(store.myOpenEntry) }}</template>
      </v-card-item>
      <v-card-text>
        <div class="text-h3 font-weight-medium" data-testid="elapsed">{{ elapsed(store.myOpenEntry) }}</div>
        <div class="text-body2 mt-1">Since {{ formatTime(store.myOpenEntry.clockIn) }}</div>
        <v-alert v-if="isStale(store.myOpenEntry)" type="warning" variant="tonal" density="compact" class="mt-3">
          This has been running a long time. If you forgot to clock out, ask a coordinator to correct your time.
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-btn color="error" variant="flat" size="large" block @click="openClockOut">Clock Out</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Ready to clock in (hidden while clocked in: one shift at a time) -->
    <h2 v-if="!store.myOpenEntry" class="text-h6 mb-2">Clock in</h2>
    <v-skeleton-loader v-if="!loaded" type="card" class="mb-6" />
    <v-card v-else-if="!store.myOpenEntry && readyShifts.length === 0" class="mb-6">
      <v-card-text class="text-center py-8 text-medium-emphasis">
        Nothing to clock in to right now. Clock-in opens {{ CLOCK_IN_EARLY_HRS }} hour before a call starts.
      </v-card-text>
    </v-card>
    <v-card v-for="s in store.myOpenEntry ? [] : readyShifts" :key="s.key" class="mb-3">
      <v-card-item>
        <template #title>{{ s.booking.gigName }}</template>
        <template #subtitle>{{ s.booking.role }}</template>
      </v-card-item>
      <v-card-text>
        <p class="text-body2">{{ s.when }}</p>
      </v-card-text>
      <v-card-actions>
        <v-btn
          color="primary"
          variant="flat"
          size="large"
          block
          :loading="store.busy && actingKey === s.key"
          :disabled="store.busy"
          @click="handleClockIn(s)"
        >
          Clock In
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Upcoming -->
    <template v-if="upcomingShifts.length">
      <h2 class="text-h6 mb-2 mt-6">Upcoming</h2>
      <v-card class="mb-6">
        <v-list>
          <v-list-item v-for="s in upcomingShifts" :key="s.key" :title="`${s.booking.gigName} — ${s.booking.role}`" :subtitle="s.when" />
        </v-list>
      </v-card>
    </template>

    <!-- History -->
    <h2 class="text-h6 mb-2 mt-6">My time</h2>
    <v-card v-if="history.length === 0" class="mb-6">
      <v-card-text class="text-center py-8 text-medium-emphasis">No time recorded yet</v-card-text>
    </v-card>
    <v-card v-else class="mb-6">
      <v-list lines="three">
        <v-list-item v-for="e in history" :key="e.id">
          <v-list-item-title>{{ shiftLabel(e) }}</v-list-item-title>
          <v-list-item-subtitle>
            {{ formatDateTime(e.clockIn) }} → {{ e.clockOut ? formatTime(e.clockOut) : '—' }}
            · {{ hoursLabel(e) }}<span v-if="e.breakMinutes"> · {{ e.breakMinutes }} min break</span>
          </v-list-item-subtitle>
          <v-list-item-subtitle v-if="e.status === 'REJECTED' && e.reviewNote" class="text-error">
            Rejected: {{ e.reviewNote }}
          </v-list-item-subtitle>
          <template #append>
            <v-chip size="small" :color="statusColor(e.status)">{{ statusLabel(e.status) }}</v-chip>
          </template>
        </v-list-item>
      </v-list>
    </v-card>

    <!-- Clock-out dialog -->
    <v-dialog v-model="clockOutDialog" max-width="420">
      <v-card>
        <v-card-title>Clock out</v-card-title>
        <v-card-text>
          <v-select
            v-model="breakMinutes"
            :items="breakOptions"
            item-title="title"
            item-value="value"
            label="Meal break taken"
          />
          <v-textarea v-model="notes" label="Notes (optional)" rows="2" auto-grow counter="1000" maxlength="1000" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="clockOutDialog = false">Cancel</v-btn>
          <v-btn color="error" variant="flat" :loading="store.busy" @click="handleClockOut">Clock Out</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import type { Booking, TimesheetEntry, TimesheetEntryStatus } from '@gigdriver/domain';
import { canClockIn, entryHours, needsAttention, CLOCK_IN_EARLY_HRS } from '@gigdriver/domain';
import { getPersonOffers } from '@gigdriver/client';
import { useAuthStore } from '../stores/auth';
import { useOrganizationStore } from '../stores/organization';
import { useTimesheetsStore } from '../stores/timesheets';

const authStore = useAuthStore();
const orgStore = useOrganizationStore();
const store = useTimesheetsStore();

const bookings = ref<Booking[]>([]);
const pageError = ref<string | null>(null);
const now = ref(new Date());
const actingKey = ref<string | null>(null);
// False until the first bookings fetch resolves, so the page doesn't flash
// "nothing to clock in to" while it's still loading.
const loaded = ref(false);

let tick: ReturnType<typeof setInterval> | undefined;

async function loadBookings() {
  if (!authStore.currentUser) return;
  try {
    bookings.value = await getPersonOffers(authStore.currentUser.uid);
  } catch (err) {
    pageError.value = err instanceof Error ? err.message : 'Failed to load your shifts';
  } finally {
    loaded.value = true;
  }
}

function start(uid: string | undefined) {
  if (!uid) return;
  store.watchMine(uid);
  loadBookings();
}

onMounted(() => {
  tick = setInterval(() => (now.value = new Date()), 15_000);
  start(authStore.currentUser?.uid);
});
// currentUser resolves asynchronously on a fresh load — see MyOffers.vue.
watch(() => authStore.currentUser?.uid, start);
onUnmounted(() => {
  clearInterval(tick);
  store.stopMineWatch();
});

const timeZone = computed(() => orgStore.currentOrg?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone);
const committed = computed(() => bookings.value.filter(b => b.state === 'ACCEPTED' || b.state === 'BOOKED'));
const bookingById = computed(() => new Map(bookings.value.map(b => [b.id, b])));

interface Shift {
  key: string;
  booking: Booking;
  callId?: string;
  when: string;
  sortTime: number;
}

// The client only approximates eligibility (it can't read the gig's state);
// the clockInToBooking function re-checks everything and its message is
// shown if the server disagrees.
const shifts = computed(() => {
  const out: Array<Shift & { ready: boolean }> = [];
  for (const booking of committed.value) {
    const decision = canClockIn({ booking, gigState: 'BOOKED', now: now.value, timeZone: booking.venueTimeZone ?? timeZone.value });
    const calls = booking.linkedCalls ?? [];
    const next = calls.find(c => c.endTime.getTime() >= now.value.getTime());
    const when = decision.ok && decision.callId
      ? callWhen(calls.find(c => c.callId === decision.callId)!)
      : next
        ? callWhen(next)
        : booking.scopeDate
          ? `Day of ${formatDate(booking.scopeDate)}`
          : '';
    if (!decision.ok && !next && !(booking.scopeDate && booking.scopeDate.getTime() >= startOfToday())) continue;
    out.push({
      key: `${booking.id}:${decision.ok ? (decision.callId ?? 'day') : 'upcoming'}`,
      booking,
      callId: decision.ok ? decision.callId : undefined,
      when,
      ready: decision.ok,
      sortTime: (next?.startTime ?? booking.scopeDate)?.getTime() ?? Infinity,
    });
  }
  return out.sort((a, b) => a.sortTime - b.sortTime);
});
const readyShifts = computed(() => shifts.value.filter(s => s.ready));
const upcomingShifts = computed(() => shifts.value.filter(s => !s.ready).slice(0, 5));

function startOfToday(): number {
  const d = new Date(now.value);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

const history = computed(() => store.myEntries.filter(e => e.status !== 'OPEN'));

async function handleClockIn(s: Shift) {
  actingKey.value = s.key;
  try {
    await store.clockIn({
      orgId: s.booking.orgId,
      gigId: s.booking.gigId,
      positionId: s.booking.positionId,
      bookingId: s.booking.id,
      callId: s.callId,
    });
  } catch {
    // surfaced via store.error
  } finally {
    actingKey.value = null;
  }
}

const clockOutDialog = ref(false);
const breakMinutes = ref(0);
const notes = ref('');
const breakOptions = [
  { title: 'None', value: 0 },
  { title: '30 minutes', value: 30 },
  { title: '1 hour', value: 60 },
];

function openClockOut() {
  breakMinutes.value = 0;
  notes.value = '';
  clockOutDialog.value = true;
}

async function handleClockOut() {
  const entry = store.myOpenEntry;
  if (!entry) return;
  try {
    await store.clockOut({
      orgId: entry.orgId,
      gigId: entry.gigId,
      entryId: entry.id,
      breakMinutes: breakMinutes.value || undefined,
      notes: notes.value.trim() || undefined,
    });
    clockOutDialog.value = false;
  } catch {
    // surfaced via store.error
    clockOutDialog.value = false;
  }
}

function dismissErrors() {
  pageError.value = null;
  store.error = null;
}

function shiftLabel(e: TimesheetEntry): string {
  const b = bookingById.value.get(e.bookingId);
  return b ? `${b.gigName ?? 'Gig'} — ${b.role ?? 'Shift'}` : 'Shift';
}

function elapsed(e: TimesheetEntry): string {
  const mins = Math.floor(entryHours(e, now.value) * 60);
  return `${Math.floor(mins / 60)}:${String(mins % 60).padStart(2, '0')}`;
}

function isStale(e: TimesheetEntry): boolean {
  return needsAttention(e, now.value);
}

function hoursLabel(e: TimesheetEntry): string {
  return `${entryHours(e).toFixed(2)} h`;
}

function callWhen(c: { type: string; startTime: Date; endTime: Date }): string {
  return `${c.type} — ${formatDateTime(c.startTime)} → ${formatTime(c.endTime)}`;
}

function formatDate(d: Date): string {
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}
function formatTime(d: Date): string {
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}
function formatDateTime(d: Date): string {
  return `${formatDate(d)}, ${formatTime(d)}`;
}

function statusLabel(s: TimesheetEntryStatus): string {
  return { OPEN: 'Clocked in', PENDING: 'Pending review', APPROVED: 'Approved', REJECTED: 'Rejected' }[s];
}
function statusColor(s: TimesheetEntryStatus): string {
  return { OPEN: 'info', PENDING: 'warning', APPROVED: 'success', REJECTED: 'error' }[s];
}
</script>
